import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Save, FileText, Building, Calendar, Tag, StickyNote, Download, Trash2, ExternalLink } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const ContractEditModal = ({ contractId, onClose, onUpdate, onDelete }) => {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Form fields
  const [customName, setCustomName] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [contractType, setContractType] = useState('');
  const [signatureDate, setSignatureDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');

  const contractTypes = [
    { value: 'financial_contract', label: 'Financiero' },
    { value: 'insurance_policy', label: 'Seguro' },
    { value: 'real_estate_contract', label: 'Inmobiliario' },
    { value: 'commercial_contract', label: 'Comercial' },
    { value: 'labor_contract', label: 'Laboral' },
    { value: 'unknown', label: 'Otro' }
  ];

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  const fetchContract = async () => {
    try {
      const data = await api.getContractFull(contractId);
      setContract(data);
      setCustomName(data.custom_name || '');
      setCounterparty(data.counterparty || '');
      setContractType(data.contract_type || '');
      setSignatureDate(data.signature_date || '');
      setExpirationDate(data.expiration_date || '');
      setNotes(data.notes || '');
    } catch (error) {
      toast.error('Error al cargar contrato');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateContractMetadata(contractId, {
        custom_name: customName || null,
        counterparty: counterparty || null,
        contract_type: contractType || null,
        signature_date: signatureDate || null,
        expiration_date: expirationDate || null,
        notes: notes || null
      });
      toast.success('Contrato actualizado');
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await api.downloadContract(contractId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = contract?.file_name || 'contrato';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Descarga iniciada');
    } catch (error) {
      toast.error('Error al descargar');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteContract(contractId);
      toast.success('Contrato eliminado');
      if (onDelete) onDelete();
      onClose();
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Activo' },
      expiring_soon: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Por vencer' },
      expired: { bg: 'bg-red-100', text: 'text-red-700', label: 'Vencido' }
    };
    const style = config[status] || config.active;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: '#1A4D2E' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between" style={{ backgroundColor: '#1A4D2E' }}>
          <div className="flex items-center gap-2 text-white">
            <FileText className="w-5 h-5" />
            <h3 className="font-semibold">Editar Contrato</h3>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* File Info */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-gray-400" />
              <div>
                <p className="font-medium text-sm">{contract?.file_name}</p>
                <p className="text-xs text-gray-500">
                  Subido: {new Date(contract?.upload_date).toLocaleDateString('es-MX')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {contract?.lifecycle_status && getStatusBadge(contract.lifecycle_status)}
              {contract?.risk_level && (() => {
                const cal = contract.analysis_result?.conclusion_recomendacion?.calificacion;
                let lvl = contract.risk_level;
                if (cal !== undefined && cal !== null) {
                  const s = parseFloat(cal);
                  if (!isNaN(s)) lvl = s > 6 ? 'high' : s > 3 ? 'medium' : 'low';
                }
                return (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    lvl === 'high' ? 'bg-red-100 text-red-700' :
                    lvl === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    Riesgos detectados: {contract.risk_score}
                  </span>
                );
              })()}
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <Tag className="w-4 h-4" /> Nombre personalizado
              </label>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Ej: Crédito Auto Toyota"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm"
                data-testid="custom-name-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <Building className="w-4 h-4" /> Contraparte
              </label>
              <input
                type="text"
                value={counterparty}
                onChange={(e) => setCounterparty(e.target.value)}
                placeholder="Ej: BBVA México"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm"
                data-testid="counterparty-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4" /> Tipo de contrato
              </label>
              <select
                value={contractType}
                onChange={(e) => setContractType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm"
                data-testid="contract-type-select"
              >
                <option value="">Seleccionar...</option>
                {contractTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4" /> Fecha de firma
              </label>
              <input
                type="date"
                value={signatureDate}
                onChange={(e) => setSignatureDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm"
                data-testid="signature-date-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-amber-500" /> Fecha de vencimiento
              </label>
              <input
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm"
                data-testid="expiration-date-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recibirás alertas 30, 15 y 7 días antes del vencimiento
              </p>
              {expirationDate && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const links = await api.getGCalLinks(contractId);
                      // Open expiration event
                      window.open(links.expiration_url, '_blank');
                      // Open reminder events after short delay
                      for (const rem of links.reminder_urls || []) {
                        setTimeout(() => window.open(rem.url, '_blank'), 800);
                      }
                      toast.success('Eventos de Google Calendar generados');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Error al generar eventos');
                    }
                  }}
                  className="mt-2 flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  data-testid="gcal-add-btn"
                >
                  <Calendar className="w-4 h-4" />
                  Agregar a Google Calendar
                  <ExternalLink className="w-3 h-3" />
                </button>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <StickyNote className="w-4 h-4" /> Notas
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Agregar notas o comentarios..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm resize-none"
                data-testid="notes-textarea"
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleDownload}
              data-testid="download-contract-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Descargar
            </Button>
            
            {!showDeleteConfirm ? (
              <Button 
                variant="outline" 
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-contract-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Eliminar
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-600">¿Seguro?</span>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Eliminando...' : 'Sí, eliminar'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  No
                </Button>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              style={{ backgroundColor: '#1A4D2E' }}
              data-testid="save-contract-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractEditModal;
